import React, { useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Flex,
    Heading,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Stack,
    Table,
    Tag,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useDisclosure,
} from '@chakra-ui/react';
import { AttachmentIcon } from '@chakra-ui/icons';
import { usePostRequest } from '@/utils/useStatefulRequests';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';
import { LoadingSpinner } from '@/components';
import { useAssignment } from '@/components/contexts/AssignmentContext/useAssignment';
import { OrgWorkdayChange, WorkdayChange, WorkdayUploadResponse } from '@/wpt-lib/interfaces/Workday';
import { Column, ListTable } from '@/components/pages/dashboard/tiles/bottomTile/bottomTileTables/ListTable';
import { PaginationToolbar } from '@/components/Table';
import { defaultTableParams } from '@/utils/tableUtils';

const renderTypeTag = (type: WorkdayChange['type']) => (
    <Tag colorScheme={type === 'add' ? 'green' : 'blue'} textTransform="capitalize">
        {type}
    </Tag>
);

const facultyColumns: Column<WorkdayChange>[] = [
    { name: 'Change', getData: (row) => renderTypeTag(row.type), width: '120px' },
    { name: 'Name', getData: (row) => row.name },
    { name: 'Workday Alt Hierarchy ID', getData: (row) => row.workdayAltHierarchyId },
];

const organisationColumns: Column<OrgWorkdayChange>[] = [
    { name: 'Change', getData: (row) => renderTypeTag(row.type), width: '120px' },
    { name: 'Name', getData: (row) => row.name },
    { name: 'Faculty', getData: (row) => row.facultyName ?? '-' },
    { name: 'Workday Alt Hierarchy ID', getData: (row) => row.workdayAltHierarchyId },
];

interface ChangesTableProps<T extends WorkdayChange> {
    heading: string;
    columns: Column<T>[];
    items: T[];
}

const SCHEMA = [
    ['code', 'Workday Alternate Hierarchy Identifier', 'FH19A60'],
    ['name', 'Name of the faculty / organisation', 'FDS School of Architecture'],
    ['OrgLevel1_id', 'Hierarchy level 1 identifier', 'FH10000'],
    ['OrgLevel1_name', 'Hierarchy level 1 name', 'University of Technology Sydney'],
    ['OrgLevel2_id', 'Hierarchy level 2 identifier', 'FH19000'],
    ['OrgLevel2_name', 'Hierarchy level 2 name', 'PROVOST'],
    ['OrgLevel3_id', 'Hierarchy level 3 identifier', 'FH19A00'],
    ['OrgLevel3_name', 'Hierarchy level 3 name', 'Faculty of Design and Society'],
    ['OrgLevel3_subtype', 'Hierarchy level 3 type', 'Faculty'],
];

const ChangesTable = <T extends WorkdayChange>({ heading, columns, items }: ChangesTableProps<T>) => {
    const [page, setPage] = useState(defaultTableParams.page);
    const [pageSize, setPageSize] = useState(defaultTableParams.pageSize);
    const [focusPage, setFocusPage] = useState(false);

    const pages = Math.max(1, Math.ceil(items.length / pageSize));
    const safePage = Math.min(page, pages - 1);
    const pageItems = useMemo(
        () => items.slice(safePage * pageSize, safePage * pageSize + pageSize),
        [items, safePage, pageSize],
    );

    return (
        <Stack gap={2}>
            <Heading as="h3" size="sm">
                {heading} ({items.length})
            </Heading>
            <Box width="full" overflowX="auto">
                <ListTable tableCaption={''} columns={columns} getActions={() => []} data={pageItems} />
            </Box>
            <Box width="full" overflowX="auto">
                <PaginationToolbar
                    label={`${heading} `}
                    setPageSize={(size) => {
                        setPageSize(size);
                        setPage(0);
                    }}
                    setNewPage={setPage}
                    pageSize={pageSize}
                    pages={pages}
                    page={safePage}
                    focusPage={focusPage}
                    setFocusPage={setFocusPage}
                />
            </Box>
        </Stack>
    );
};

const WorkdayUpload = () => {
    const { currentAssignment } = useAssignment();

    const {
        makeRequest: upload,
        error,
        loading,
        responseData: data,
        clear,
    } = usePostRequest<WorkdayUploadResponse, FormData>();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedFile(event.target.files?.[0] ?? null);
    };

    const handleUpload = async () => {
        if (!selectedFile) return;

        const formData = new FormData();
        formData.append('file', selectedFile);

        await upload('/organisation/workday', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            params: { assignment: currentAssignment?.id },
        });
    };

    const handleClose = () => {
        if (loading) return;
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';

        clear();
        onClose();
    };

    return (
        <>
            <Flex justify="flex-start">
                <Button leftIcon={<AttachmentIcon />} onClick={onOpen}>
                    Upload Workday Alternate Hierarchy
                </Button>
            </Flex>

            <Modal isOpen={isOpen} onClose={handleClose} size="5xl" closeOnOverlayClick={!loading}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Upload Workday Alternate Hierarchy</ModalHeader>
                    <ModalCloseButton isDisabled={loading} />
                    <ModalBody>
                        <>
                            {error && (
                                <SimpleAlert variant={'left-accent'} mb={4}>
                                    An error occurred uploading the file. Please refresh page and try again.
                                </SimpleAlert>
                            )}

                            {loading && <LoadingSpinner />}

                            {!loading && !data && (
                                <>
                                    <Text mb={4}>
                                        Work Planning Tool uses Workday Alternate Hierarchy to map staff to faculties
                                        and organisation units. Faculties and organisation units within WPT can be added
                                        and updated from Workday Alternate Hierarchy CSV file with the following format:
                                    </Text>
                                    <Box mb={8} overflowX="auto">
                                        <Table size="sm" variant="simple">
                                            <Thead>
                                                <Tr>
                                                    <Th>Column</Th>
                                                    <Th>Description</Th>
                                                    <Th>Example</Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {SCHEMA.map(([column, description, example], index) => (
                                                    <Tr key={index}>
                                                        <Td>
                                                            <code>{column}</code>
                                                        </Td>
                                                        <Td>{description}</Td>
                                                        <Td>{example}</Td>
                                                    </Tr>
                                                ))}
                                            </Tbody>
                                        </Table>
                                    </Box>
                                    <Input
                                        type="file"
                                        accept=".csv"
                                        ref={fileInputRef}
                                        onChange={handleFileChange}
                                        disabled={loading}
                                        p={1}
                                    />
                                </>
                            )}

                            {!loading && data && (
                                <Stack gap={6}>
                                    <Text>Upload complete, faculties and organisations changed below.</Text>
                                    <ChangesTable heading="Faculties" columns={facultyColumns} items={data.faculties} />
                                    <ChangesTable
                                        heading="Organisations"
                                        columns={organisationColumns}
                                        items={data.organisations}
                                    />
                                </Stack>
                            )}
                        </>
                    </ModalBody>
                    <ModalFooter>
                        <Button mr={3} onClick={handleClose} isDisabled={loading}>
                            {data ? 'Close' : 'Cancel'}
                        </Button>
                        {!data && (
                            <Button
                                colorScheme="blue"
                                isLoading={loading}
                                isDisabled={!selectedFile || loading}
                                onClick={handleUpload}
                            >
                                Upload
                            </Button>
                        )}
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};
export default WorkdayUpload;
